import { z } from 'zod'

import type { StatusConfig } from '../templates'
import { ModuleReferenceInfo } from '../module-reference'
import { Task, TaskSchema } from './base'

// Unified user type for all task-related contexts
export type TaskUser = {
  id: string
  memberId?: string | null
  userId?: string | null
  email: string
  // Name fields - at least one should be present
  name?: string
  displayName?: string
  firstName?: string
  lastName?: string
  // Avatar/image
  avatarUrl?: string
  image?: string
}

// Simplified assignment entries - use the same structure for all contexts
export type TaskAssigneeEntry = {
  user: TaskUser
}

// For backward compatibility - now just an alias
export type TaskAssignmentEntry = TaskAssigneeEntry

// Task type with assignments - extends base Task with all possible relations
export interface TaskWithAssignments extends Task {
  // Assignment data in both formats for compatibility
  taskAssignments?: Array<{
    id: string
    taskId: string
    userId?: string | null
    memberId?: string | null
    user: TaskUser
  }>
  assignees?: TaskAssigneeEntry[]

  // Creator information (resolved user who created the task)
  creator?: TaskUser | null

  // Module information
  moduleInfo?: ModuleReferenceInfo | null
  customFields?: Record<string, any>

  // Project settings with status configurations (moduleInfo has display data)
  projectSettings?: {
    globalStatuses?: StatusConfig[]
  } | null

  // Available statuses for this specific task based on its project and group context
  availableStatuses?: StatusConfig[] | null

  // Counts for performance
  completedSubtaskCount?: number
  commentCount?: number // Count of comments on this task

  // Relations
  taskStatus?: any
  group?: any
  sprint?: any

  // Recurring task relations
  parentRecurringTask?: TaskWithAssignments | null
  recurringTaskInstances?: TaskWithAssignments[] | null

  // Subtask relations
  parentTask?: TaskWithAssignments | null
  subtasks?: TaskWithAssignments[] | null

  // Dependency relations
  dependenciesFrom?: any[] | null
  dependenciesTo?: any[] | null
}

// Type guard for TaskWithAssignments
export function isTaskWithAssignments(
  task: unknown,
): task is TaskWithAssignments {
  return (
    typeof task === 'object' &&
    task !== null &&
    'id' in task &&
    'organizationId' in task &&
    'name' in task &&
    (!('assignees' in task) ||
      Array.isArray((task as TaskWithAssignments).assignees))
  )
}

// Task status count schema
export const TaskStatusCountSchema = z.object({
  statusType: z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
  }),
  count: z.number().default(0),
})

export type TaskStatusCount = z.infer<typeof TaskStatusCountSchema>

/**
 * Completed/canceled task counts for a task list view, split by task group
 * (`groupId: null` = Ungrouped). Returned by `GET /tasks/completed-counts`
 * with the same filters as `GET /tasks`, so a list can say how many rows
 * "Hide completed tasks" is holding back — per section, and for the current
 * search/filter.
 */
export interface TaskCompletedCounts {
  total: number
  groups: Array<{ groupId: string | null; count: number }>
}
